import React, { useEffect, useRef, useState } from 'react';
import { LockKeyhole, Minus, Plus, ShieldCheck, ShoppingBag, Trash2, X } from 'lucide-react';
import { money } from './api.js';
import { Photo } from './commerce.js';
import Fulfillment from './Fulfillment.jsx';
import ShippingPicker from './ShippingPicker.jsx';
import './commerce.css';

const FOCUSABLE = 'button:not([disabled]), a[href], input:not([disabled]), [tabindex]:not([tabindex="-1"])';
const CLOSE_MS = 300;

// The cart, sliding in from the right (a full-width sheet on phones). `cart` is the object returned by useCart.
export default function CartDrawer({ cart, payment, onPrivacy, onReturns }) {
  const { items, open, busy, message } = cart;
  const [mounted, setMounted] = useState(open);
  const panel = useRef(null);
  const close = () => cart.setOpen(false);

  // Stay on the page for the closing slide, then leave it.
  useEffect(() => {
    if (open) { setMounted(true); return undefined; }
    const timer = setTimeout(() => setMounted(false), CLOSE_MS);
    return () => clearTimeout(timer);
  }, [open]);

  // While open: the page behind does not scroll, focus stays inside, and Escape closes. Closing hands focus back.
  useEffect(() => {
    if (!open || !mounted) return undefined;
    const before = document.activeElement, body = document.body;
    const saved = { overflow: body.style.overflow, paddingRight: body.style.paddingRight };
    const scrollbar = window.innerWidth - document.documentElement.clientWidth;
    body.style.overflow = 'hidden';
    if (scrollbar > 0) body.style.paddingRight = `${scrollbar}px`;
    panel.current?.focus({ preventScroll: true });
    const onKey = (event) => {
      if (event.key === 'Escape') { event.preventDefault(); cart.setOpen(false); return; }
      if (event.key !== 'Tab' || !panel.current) return;
      const stops = [...panel.current.querySelectorAll(FOCUSABLE)];
      if (!stops.length) { event.preventDefault(); return; }
      const first = stops[0], last = stops[stops.length - 1], at = document.activeElement;
      if (!panel.current.contains(at) || at === panel.current) { event.preventDefault(); (event.shiftKey ? last : first).focus(); }
      else if (event.shiftKey && at === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && at === last) { event.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      body.style.overflow = saved.overflow;
      body.style.paddingRight = saved.paddingRight;
      before?.focus?.({ preventScroll: true });
    };
  }, [open, mounted]);

  if (!mounted) return null;
  const closing = !open;
  const providerNote = !message && payment?.provider === 'square' && payment?.checkout_message;
  return <div className={`cart-backdrop${closing ? ' is-closing' : ''}`} role="presentation" onMouseDown={(event) => event.target === event.currentTarget && close()}>
    <aside ref={panel} className="cart-drawer" role="dialog" aria-modal="true" aria-label="Shopping cart" aria-hidden={closing || undefined} tabIndex={-1}>
      <header className="cart-head">
        <div><h2>Your cart</h2><small>{items.length} product line{items.length === 1 ? '' : 's'}</small></div>
        <button type="button" className="cart-close" onClick={close} aria-label="Close cart"><X aria-hidden="true" /></button>
      </header>
      {items.length > 0 ? <ul className="cart-items">
        {items.map((item) => <li className="cart-item" key={item.key}>
          <span className="cart-thumb"><Photo src={item.image} sizes="96px" width={240} /></span>
          <div className="cart-item-name"><strong>{item.name}</strong>{item.option_label && <small>{item.option_label}</small>}</div>
          <b className="cart-item-price">{money(item.price_cents, item.currency)}</b>
          <div className="cart-stepper" role="group" aria-label={`Quantity for ${item.name}`}>
            <button type="button" disabled={busy} onClick={() => cart.setQuantity(item, item.quantity - 1)} aria-label="Decrease quantity"><Minus aria-hidden="true" /></button>
            <span aria-live="polite">{item.quantity}</span>
            <button type="button" disabled={busy || item.quantity >= 20} onClick={() => cart.setQuantity(item, item.quantity + 1)} aria-label="Increase quantity"><Plus aria-hidden="true" /></button>
          </div>
          <button type="button" className="cart-remove" disabled={busy} onClick={() => cart.remove(item)} aria-label={`Remove ${item.name}`}><Trash2 aria-hidden="true" /></button>
        </li>)}
      </ul> : <div className="cart-empty">
        <span className="cart-empty-icon"><ShoppingBag aria-hidden="true" /></span>
        <strong>Your cart is empty.</strong>
        <small>Add a product to begin an order.</small>
      </div>}
      <footer className="cart-foot">
        <div className="cart-note" aria-live="polite">
          {message ? <p className="cart-message">{message}</p> : providerNote ? <p className="cart-provider"><ShieldCheck aria-hidden="true" />{providerNote}</p> : null}
        </div>
        {items.length > 0 && <Fulfillment cart={cart} />}
        {items.length > 0 && <ShippingPicker cart={cart} />}
        <div className="cart-total">
          {cart.fulfillment === 'ship' && cart.shipOption ? <>
            <span className="cart-total-sub">Subtotal</span><b className="cart-total-sub">{money(cart.totalCents, items[0]?.currency || 'CAD')}</b>
            <span className="cart-total-sub">Shipping</span><b className="cart-total-sub">{money(cart.shipOption.cents, items[0]?.currency || 'CAD')}</b>
            <span>Total</span><strong>{money(cart.totalCents + cart.shipOption.cents, items[0]?.currency || 'CAD')}</strong>
          </> : <><span>Subtotal</span><strong>{money(cart.totalCents, items[0]?.currency || 'CAD')}</strong></>}
          <small>{cart.cash ? 'Pickup order: no shipping. You pay this in cash when you collect it.' : cart.fulfillment === 'pickup' ? 'Pickup order: no shipping. Applicable tax is confirmed during hosted checkout.' : cart.shipOption ? 'Applicable tax is confirmed during hosted checkout.' : cart.shippingOn ? 'Add where it is going to see shipping. Applicable tax is confirmed during hosted checkout.' : 'Shipping and applicable tax are confirmed during hosted checkout.'}</small>
        </div>
        <button type="button" className="shop-btn wide" disabled={busy || !items.length} aria-busy={busy || undefined} onClick={cart.checkoutCart}>
          {busy ? <span className="shop-spinner cart-spin" aria-hidden="true" /> : <ShieldCheck aria-hidden="true" />}{cart.cash ? 'Place order' : 'Secure checkout'}
        </button>
        <p className="cart-final">All sales final; see <a href="/store/returns" onClick={(event) => { if (onReturns) { event.preventDefault(); onReturns(); } }}>Returns &amp; Warranty</a>.</p>
        <div className="cart-links">
          <button type="button" onClick={onPrivacy}><LockKeyhole aria-hidden="true" />Privacy</button>
          <button type="button" onClick={cart.clear} disabled={busy || !items.length}><Trash2 aria-hidden="true" />Clear cart</button>
        </div>
      </footer>
    </aside>
  </div>;
}
